import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, User, Calendar } from "lucide-react";

const fmt = (n) => {
  if (n == null || n === "") return "—";
  const num = Number(n);
  return isNaN(num) ? String(n) : "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function SalaryRevisionTimeline({ employeeId }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masked, setMasked] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/employees/${employeeId}/salary-revisions`);
        setRevisions(data.revisions || []);
        setMasked(!!(data.revisions?.[0]?._masked_for_hr_maker));
      } catch {
        setRevisions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [employeeId]);

  if (loading) return null;
  if (revisions.length === 0) return null;

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4"
      data-testid="salary-revision-timeline"
    >
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-emerald-600" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Salary Revision Timeline ({revisions.length})
        </div>
        {masked && (
          <span className="text-[10px] text-slate-400 ml-2">
            (amounts hidden — hr_admin or super_admin only)
          </span>
        )}
      </div>
      <div className="relative pl-6">
        {/* Vertical line */}
        <div className="absolute left-2 top-0 bottom-0 w-px bg-emerald-200"></div>
        <div className="space-y-4">
          {revisions.map((rev) => (
            <div key={rev.approval_request_id} className="relative" data-testid={`revision-node-${rev.approval_request_id}`}>
              {/* Node dot */}
              <div className="absolute -left-[19px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-1 ring-emerald-200"></div>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] text-slate-500">
                    <Calendar className="h-3 w-3" />
                    {fmtDT(rev.applied_at)}
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(rev.comp_diff || {}).map(([k, v]) => (
                      <div
                        key={k}
                        className="text-sm bg-emerald-50/60 border border-emerald-200 rounded px-2 py-1"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-slate-500 line-through">
                            {typeof v.before === "number" ? fmt(v.before) : (v.before ?? "—")}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono text-emerald-700 font-semibold">
                            {typeof v.after === "number" ? fmt(v.after) : (v.after ?? "—")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {rev.decision_reason && (
                    <div className="mt-1.5 text-[11px] text-slate-500 italic">
                      "{rev.decision_reason}"
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1 shrink-0">
                  <User className="h-3 w-3" />
                  by <span className="font-semibold text-slate-700">{rev.approver_name}</span>
                  {rev.maker_name && rev.maker_name !== rev.approver_name && (
                    <span className="text-slate-400"> (req: {rev.maker_name})</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
